import React, { useState } from "react";
import CommonFormBuilder from "../CommonComponenets/FormBuilder";
import {TYMPANOGRAM_EXTRACT_URL} from "../../platform/config/api.config"

const valueToText = (value) =>
  value === undefined || value === null ? "" : String(value);

const setIfPresent = (target, key, value) => {
  if (value !== undefined && value !== null && value !== "") {
    target[key] = valueToText(value);
  }
};

export function AuditoryAdvancedForm({ onBack, mode }) {
  const [values, setValues] = useState({});
  const [vasScoresVisible, setVasScoresVisible] = useState(true);
  const [hhiaScoresVisible, setHhiaScoresVisible] = useState(true);

  const HHIA_QUESTIONS = [
    "Does a hearing problem cause you to use the phone less often than you would like?",
    "Does a hearing problem cause you to feel embarrassed when meeting new people?",
    "Does a hearing problem cause you to avoid groups of people?",
    "Does a hearing problem make you irritable?",
    "Does a hearing problem cause you to feel frustrated when talking to members of your family?",
    "Does a hearing problem cause you difficulty when attending a party?",
    "Does a hearing problem cause you difficulty hearing/understanding coworkers, clients, or customers?",
    "Do you feel handicapped by a hearing problem?",
    "Does a hearing problem cause you difficulty when visiting friends, relatives or neighbors?",
    "Does a hearing problem cause you to feel frustrated when talking to coworkers or clients?",
    "Does a hearing problem cause you difficulty in the movies or theater?",
    "Does a hearing problem cause you to be nervous?",
    "Does a hearing problem cause you to visit friends/relatives less often than you would like?",
    "Does a hearing problem cause you to have arguments with family members?",
    "Does a hearing problem cause you difficulty when listening to TV or radio?",
    "Does a hearing problem cause you to go shopping less often than you would like?",
    "Does any problem or difficulty with your hearing upset you at all?",
    "Does a hearing problem cause you to want to be by yourself?",
    "Does a hearing problem cause you to talk to family members less often than you would like?",
    "Do you feel that any difficulty with your hearing limits your personal or social life?",
    "Does a hearing problem cause you difficulty in a restaurant with relatives or friends?",
    "Does a hearing problem cause you to feel depressed?",
    "Does a hearing problem cause you to listen to TV or radio less than you would like?",
    "Does a hearing problem cause you to feel uncomfortable when talking to friends?",
    "Does a hearing problem cause you to feel left out when you are with a group of people?"
  ];

  // =========================
  // CALCULATIONS
  // =========================
  const getSeverityLabel = (score) => {
    const val = Number(score || 0);
    if (val <= 2) return "Minimal";
    if (val <= 4) return "Mild";
    if (val <= 6) return "Moderate";
    if (val <= 8) return "Severe";
    return "Very Severe";
  };

  const computeHHIA = (v) => {
    let social = 0;
    let emotional = 0;

    const getVal = (x) => Number(x || 0);

    const socialQs    = [1,3,6,7,9,11,13,15,16,19,21,23];
    const emotionalQs = [2,4,5,8,10,12,14,17,18,20,22,24,25];

    socialQs.forEach(i    => social    += getVal(v[`hhia_${i}`]));
    emotionalQs.forEach(i => emotional += getVal(v[`hhia_${i}`]));

    const total = social + emotional;
    const totalPercent    = (total    / 100) * 100;
    const socialPercent   = (social   / 48)  * 100;
    const emotionalPercent= (emotional/ 52)  * 100;

    let interpretation = "";
    if (totalPercent <= 16) interpretation = "No handicap";
    else if (totalPercent <= 42) interpretation = "Mild to Moderate";
    else interpretation = "Significant";

    return {
      social, emotional, total,
      totalPercent:     totalPercent.toFixed(1),
      socialPercent:    socialPercent.toFixed(1),
      emotionalPercent: emotionalPercent.toFixed(1),
      interpretation
    };
  };

  // =========================
  // HANDLE CHANGE
  // =========================
  const COSI_SITUATION_OPTIONS = [
    { label: "Conversation with 1 or 2 in quiet",    value: "conversation_1_2_quiet" },
    { label: "Conversation with 1 or 2 in noise",    value: "conversation_1_2_noise" },
    { label: "Conversation with group in quiet",     value: "conversation_group_quiet" },
    { label: "Conversation with group in noise",     value: "conversation_group_noise" },
    { label: "Television/Radio @ normal volume",     value: "tv_radio_normal_volume" },
    { label: "Familiar speaker on phone",            value: "familiar_speaker_phone" },
    { label: "Unfamiliar speaker on phone",          value: "unfamiliar_speaker_phone" },
    { label: "Hearing phone ring from another room", value: "phone_ring_other_room" },
    { label: "Hear front door bell or knock",        value: "door_bell_knock" },
    { label: "Hear traffic",                         value: "hear_traffic" },
    { label: "Increased social contact",             value: "increased_social_contact" },
    { label: "Feel embarrassed or stupid",           value: "feel_embarrassed_stupid" },
    { label: "Feeling left out",                     value: "feeling_left_out" },
    { label: "Feeling upset or angry",               value: "feeling_upset_angry" },
    { label: "Church or meeting",                    value: "church_meeting" },
    { label: "Other",                                value: "other" }
  ];

  const cosiSituationLabelByValue = Object.fromEntries(
    COSI_SITUATION_OPTIONS.map((o) => [o.value, o.label])
  );

  const COSI_RANK_OPTIONS = ["1", "2", "3", "4", "5"];

  const syncCosiGoalsFromRankedSelections = (v) => {
    const selected = v.hearing_situations || [];
    const ranks = v.hearing_situations_rank || {};
    const sorted = [...selected].sort(
      (a, b) => Number(ranks[a] || 99) - Number(ranks[b] || 99)
    );
    v.cosi_goals = sorted.map((situationValue) => ({
      goal: cosiSituationLabelByValue[situationValue] || situationValue,
      priority: ranks[situationValue] || ""
    }));
    return v;
  };

  const handleCosiSituationsChange = (selected, ranks) => {
    setValues((prev) =>
      syncCosiGoalsFromRankedSelections({
        ...prev,
        hearing_situations: selected,
        hearing_situations_rank: ranks,
      })
    );
  };

  const handleChange = (name, value) => {
    setValues((prev) => {
      const updated = { ...prev, [name]: value };

      if (name === "emotional_vas") {
        updated.emotional_severity = getSeverityLabel(value);
      }
      if (name === "social_vas") {
        updated.social_severity = getSeverityLabel(value);
      }

      if (name === "hearing_situations" || name === "hearing_situations_rank") {
        syncCosiGoalsFromRankedSelections(updated);
      }

      if (name.startsWith("hhia_")) {
        const hhia = computeHHIA(updated);
        return {
          ...updated,
          hhia_social:             hhia.social,
          hhia_emotional:          hhia.emotional,
          hhia_total:              hhia.total,
          hhia_total_percent:      hhia.totalPercent,
          hhia_social_percent:     hhia.socialPercent,
          hhia_emotional_percent:  hhia.emotionalPercent,
          hhia_interpretation:     hhia.interpretation
        };
      }

      return updated;
    });
  };

  // =========================
  // SCHEMAS
  // =========================

  // Main: title + Back button (no scales here — scales are always shown)
  const mainSchema = {
    title: "Additional Auditory Profile",
    actions: [{ type: "back", label: "Back" }],
    sections: [{
      title: null,
      fields: [
      {
        name: "enable_vas",
        label: "Subjective Rating Scales (Hearing Loss)",
        type: "radio",
        options: ["Yes", "No"]
      },
      {
        name: "enable_hhia",
        label: "HHIA (Hearing Handicap Inventory for Adults)",
        type: "radio",
        options: ["Yes", "No"]
      },
      {
        name: "enable_cosi",
        label: "COSI (Client Oriented Scale of Improvement)",
        type: "radio",
        options: ["Yes", "No"]
      }
      ]
    }]
  };

  // VAS - own Doctor View toggle
  const vasSchema = {
    title: "Subjective Rating Scales (Hearing Loss)",
    enableScoreToggle: true,
    actions: [{ type: "toggle-show-scores" }],
    sections: [{
      title: null,
      fields: [
        { type: "info-text", text: "0 = none, 10 = worst possible" },
        { name: "emotional_vas", label: "Analogue Scale : Emotional (0-10)", type: "scale-slider", min: 0, max: 10 },
        ...(vasScoresVisible ? [{ name: "emotional_severity", label: "Emotional Severity", type: "score-box" }] : []),
        { name: "social_vas", label: "Analogue Scale : Social / Situational (0-10)", type: "scale-slider", min: 0, max: 10 },
        ...(vasScoresVisible ? [{ name: "social_severity", label: "Social Severity", type: "score-box" }] : [])
      ]
    }]
  };

  // HHIA - own Doctor View toggle
  const hhiaSchema = {
    title: "HHIA (Hearing Handicap Inventory for Adults)",
    enableScoreToggle: true,
    actions: [{ type: "toggle-show-scores" }],
    sections: [{
      title: null,
      fields: [
        ...HHIA_QUESTIONS.map((q, i) => ({
          name: `hhia_${i + 1}`,
          label: `${i + 1}. ${q}`,
          type: "radio-matrix",
          options: hhiaScoresVisible
            ? [{ label: "No (0)", value: 0 }, { label: "Sometimes (2)", value: 2 }, { label: "Yes (4)", value: 4 }]
            : [{ label: "No", value: 0 }, { label: "Sometimes", value: 2 }, { label: "Yes", value: 4 }]
        })),
        { type: "info-text", text: "Scoring: No=0, Sometimes=2, Yes=4" },
        ...(hhiaScoresVisible ? [
          { name: "hhia_social",            label: "Social Score (/48)",    type: "score-box" },
          { name: "hhia_emotional",         label: "Emotional Score (/52)", type: "score-box" },
          { name: "hhia_total",             label: "Total Score (/100)",    type: "score-box" },
          { name: "hhia_social_percent",    label: "Social %",              type: "score-box" },
          { name: "hhia_emotional_percent", label: "Emotional %",           type: "score-box" },
          { name: "hhia_total_percent",     label: "Total %",               type: "score-box" },
          { name: "hhia_interpretation",    label: "Interpretation",        type: "score-box" }
        ] : [])
      ]
    }]
  };

  // COSI follow-up fields only (Step 1 goals use CosiSituationSelector below)
  const cosiFollowUpSchema = {
    sections: [{
      title: null,
      fields: [
        { type: "info-text", label: "Step-2: Post-Intervention - Degree of Change", showIf: { field: "mode", equals: "followup" } },
        {
          type: "dynamic-section",
          name: "cosi_change",
          showIf: { field: "mode", equals: "followup" },
          fields: [
            { name: "goal",   label: "Goal",             type: "input" },
            { name: "change", label: "Degree of Change", type: "radio",
              options: ["Much better","Better","Slightly better","No change","Worse"] }
          ]
        },

        { type: "info-text", label: "Step 3: Post-Intervention - Final Ability Rating", showIf: { field: "mode", equals: "followup" } },
        {
          type: "dynamic-section",
          name: "cosi_final",
          showIf: { field: "mode", equals: "followup" },
          fields: [
            { name: "goal",  label: "Goal",                type: "input" },
            { name: "final", label: "Final Ability Rating", type: "radio",
              options: ["Hardly ever","Occasionally","Half the time","Most of the time","Almost always"] }
          ]
        },

        { type: "subheading", label: "Counseling Summary", showIf: { field: "mode", equals: "followup" } },
        { name: "understanding", label: "Patient's understanding of hearing loss", type: "input", showIf: { field: "mode", equals: "followup" } },
        { name: "goals",         label: "Expectations / goals",                   type: "input", showIf: { field: "mode", equals: "followup" } },
        { name: "education",     label: "Education provided",                     type: "input", showIf: { field: "mode", equals: "followup" } },
        { name: "next_steps",    label: "Recommended next steps",                 type: "input", showIf: { field: "mode", equals: "followup" } }
      ]
    }]
  };

  const allValues = { ...values, mode };

  return (
    <div>
      {/* Title + Back button */}
      <CommonFormBuilder
        schema={mainSchema}
        values={allValues}
        onChange={handleChange}
        layout="nested"
        onAction={(type) => { if (type === "back") onBack(); }}
      />

      {/* VAS - own Doctor View toggle */}
      {values.enable_vas === "Yes" && (
        <CommonFormBuilder
          schema={vasSchema}
          values={allValues}
          onChange={handleChange}
          layout="nested"
          showScores={vasScoresVisible}
          onAction={(type) => {
            if (type === "toggle-show-scores") setVasScoresVisible(v => !v);
          }}
        />
      )}

      {/* HHIA - own Doctor View toggle */}
      {values.enable_hhia === "Yes" && (
        <CommonFormBuilder
          schema={hhiaSchema}
          values={allValues}
          onChange={handleChange}
          layout="nested"
          showScores={hhiaScoresVisible}
          onAction={(type) => {
            if (type === "toggle-show-scores") setHhiaScoresVisible(v => !v);
          }}
        />
      )}

      {/* COSI + Counseling - no toggle */}
      {values.enable_cosi === "Yes" && (
        <>
          <CosiSituationSelector
            options={COSI_SITUATION_OPTIONS}
            selected={values.hearing_situations || []}
            ranks={values.hearing_situations_rank || {}}
            maxSelect={5}
            rankOptions={COSI_RANK_OPTIONS}
            onChange={handleCosiSituationsChange}
          />
          <CommonFormBuilder
            schema={cosiFollowUpSchema}
            values={allValues}
            onChange={handleChange}
            layout="nested"
          />
        </>
      )}
    </div>
  );
}

function CosiSituationSelector({
  options,
  selected = [],
  ranks = {},
  maxSelect = 5,
  rankOptions = ["1", "2", "3", "4", "5"],
  onChange,
}) {
  const toggleOption = (optValue) => {
    const isChecked = selected.includes(optValue);
    if (isChecked) {
      const nextSelected = selected.filter((v) => v !== optValue);
      const nextRanks = { ...ranks };
      delete nextRanks[optValue];
      onChange(nextSelected, nextRanks);
      return;
    }
    if (selected.length >= maxSelect) return;
    const nextSelected = [...selected, optValue];
    const nextRanks = { ...ranks };
    const used = new Set(Object.values(nextRanks));
    const autoRank = rankOptions.find((r) => !used.has(r));
    if (autoRank) nextRanks[optValue] = autoRank;
    onChange(nextSelected, nextRanks);
  };

  const setRank = (optValue, rank) => {
    const nextRanks = { ...ranks };
    Object.keys(nextRanks).forEach((key) => {
      if (nextRanks[key] === rank) delete nextRanks[key];
    });
    if (rank) nextRanks[optValue] = rank;
    else delete nextRanks[optValue];
    onChange(selected, nextRanks);
  };

  const labelByValue = Object.fromEntries(options.map((o) => [o.value, o.label]));
  const sortedSelected = [...selected].sort(
    (a, b) => Number(ranks[a] || 99) - Number(ranks[b] || 99)
  );

  return (
    <div className="fb-card" style={{ marginBottom: 16, padding: "16px 18px" }}>
      <div
        style={{
          fontSize: 15,
          fontWeight: 700,
          color: "#0f172a",
          marginBottom: 14,
          paddingBottom: 8,
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        Client oriented scale of improvement (COSI)
      </div>

      <label className="form-label" style={{ display: "block", marginBottom: 6 }}>
        Step 1: Pre-Intervention — Identify listening goals (choose up to {maxSelect})
      </label>
      <p style={{ margin: "0 0 12px", fontSize: 13, color: "#64748b" }}>
        Select the situations that matter most. Then rank each selected goal below (1 = highest
        priority).
      </p>

      <div className="fb-inline-group">
        {options.map((opt) => {
          const isChecked = selected.includes(opt.value);
          const atMax = !isChecked && selected.length >= maxSelect;
          return (
            <label
              key={opt.value}
              className="form-check-label form-check"
              style={{
                margin: 0,
                opacity: atMax ? 0.5 : 1,
                background: isChecked ? "#f1f5f9" : "transparent",
                borderRadius: 8,
                padding: "4px 8px",
              }}
            >
              <input
                type="checkbox"
                checked={isChecked}
                disabled={atMax}
                onChange={() => toggleOption(opt.value)}
              />
              {opt.label}
            </label>
          );
        })}
      </div>

      {sortedSelected.length > 0 && (
        <div
          style={{
            marginTop: 16,
            padding: "14px 16px",
            borderRadius: 12,
            background: "#f8faff",
            border: "1px solid #dbeafe",
          }}
        >
          <div
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: "#1e40af",
              marginBottom: 10,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
            }}
          >
            Selected goals — priority ranking
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {sortedSelected.map((optValue) => {
              const rank = ranks[optValue];
              return (
                <div
                  key={optValue}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 10,
                    padding: "10px 12px",
                    borderRadius: 10,
                    background: "#fff",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <span
                    style={{
                      fontWeight: 800,
                      fontSize: 15,
                      color: "#2563eb",
                      minWidth: 36,
                      textAlign: "center",
                    }}
                  >
                    ({rank || "—"})
                  </span>
                  <span style={{ flex: 1, fontSize: 14, fontWeight: 600, color: "#111827" }}>
                    {labelByValue[optValue]}
                  </span>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      margin: 0,
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#64748b",
                    }}
                  >
                    Priority
                    <select
                      className="form-select form-select-sm"
                      style={{ width: 72, minHeight: 32 }}
                      value={rank || ""}
                      onChange={(e) => setRank(optValue, e.target.value)}
                    >
                      <option value="">—</option>
                      {rankOptions.map((r) => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <p
        style={{
          margin: sortedSelected.length > 0 ? "14px 0 0" : "12px 0 0",
          fontSize: 13,
          color: "#64748b",
        }}
      >
        In which specific situations do you most want to hear better?
      </p>
      <p style={{ margin: "6px 0 0", fontSize: 12, color: "#94a3b8" }}>
        {selected.length} of {maxSelect} selected
      </p>
    </div>
  );
}

export function AuditoryAdvancedFormObj({ onBack, mode  }) {
  const [values, setValues] = useState({});

  const applyTympanogramResult = (payload) => {
    const data = payload?.data || payload || {};
    const right = data.right_ear || data.rightEar || {};
    const left = data.left_ear || data.leftEar || {};

    setValues((prev) => {
      const peakPressure = { ...(prev.peak_pressure || {}) };
      const staticCompliance = { ...(prev.static_compliance || {}) };
      const ecv = { ...(prev.ecv || {}) };

      setIfPresent(peakPressure, "peak_pressure_r", right.pressure);
      setIfPresent(peakPressure, "peak_pressure_l", left.pressure);
      setIfPresent(staticCompliance, "static_compliance_r", right.compliance);
      setIfPresent(staticCompliance, "static_compliance_l", left.compliance);
      setIfPresent(ecv, "ecv_r", right.volume);
      setIfPresent(ecv, "ecv_l", left.volume);

      return {
        ...prev,
        peak_pressure: peakPressure,
        static_compliance: staticCompliance,
        ecv
      };
    });
  };

  const extractTympanogram = async (file) => {
    if (!(file instanceof File || file instanceof Blob)) return;

    const formData = new FormData();
    formData.append("file", file);
const token = localStorage.getItem("access_token");
    const response = await fetch(TYMPANOGRAM_EXTRACT_URL, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData
    });

    if (!response.ok) {
      throw new Error("Tympanogram extraction failed");
    }

    applyTympanogramResult(await response.json());
  };

  const handleChange = (name, value) => {
    setValues((prev) => ({ ...prev, [name]: value }));

    if (name === "tympanometry_report") {
      extractTympanogram(value).catch((error) => {
        console.error(error);
      });
    }
  };

  const FREQUENCIES = ["500", "1000", "2000", "4000"];

  const reflexOptions = [
    { label: "Present", value: 0 },
    { label: "Elevated", value: 1 },
    { label: "Absent", value: 2 },
    { label: "Could Not Test", value: 3}
  ];

  const thresholdOptions = Array.from({ length: 21 }).map((_, i) => ({
    label: `${i * 5}`,
    value: i * 5
  }));

  const schema = {
    title: "Auditory Assessment",
    actions: [{ type: "back", label: "Back" }],

    sections: [
      // =========================
      // ACOUSTIC REFLEX
      // =========================
      {
        title: null,
        fields: [
                  /* ===================== TYMPANOMETRY ===================== */
        {
          type: "accordion",
          name: "tympanometry_section",
          label: "Tympanometry",
          defaultOpen: false,
          children: [
            {
              type: "row",
              columns: 2,
              fields: [
                {
                  type: "attach-file",
                  name: "tympanometry_report",
                  accept: "application/pdf,image/*",
                  title: "Tympanometry",
                  multiple: false,
                  previewSize: { width: 400, height: 400 },
                  hideInputAfterSelect: true
                },
                // {
                //   type: "attach-file",
                //   name: "tympanometry_report_left",
                //   accept: "application/pdf,image/*",
                //   title: "Tympanometry - Left",
                //   multiple: false,
                //   previewSize: { width: 400, height: 400 },
                //   hideInputAfterSelect: true
                // }
              ]
            },
            {
              type: "paired-select",
              right: { name: "tymp_type_r", title: "Tympanogram Type - Right Ear" },
              left: { name: "tymp_type_l", title: "Tympanogram Type - Left Ear" },
              options: [
                { label: "Type A", value: "A" },
                { label: "Type As", value: "As" },
                { label: "Type Ad", value: "Ad" },
                { label: "Type B (Normal ECV)", value: "B_normal" },
                { label: "Type B (Small ECV)", value: "B_small" },
                { label: "Type B (Large ECV)", value: "B_large" },
                { label: "Type C", value: "C" },
                { label: "Could Not Test", value: "could_not"}
              ]
            },
            {
              type: "paired-text",
              name: "peak_pressure",
              pairs: [
                { name: "peak_pressure_r", title: "Peak Pressure (daPa) – Right" },
                { name: "peak_pressure_l", title: "Peak Pressure (daPa) – Left" }
              ]
            },
            {
              type: "paired-text",
              name: "static_compliance",
              pairs: [
                { name: "static_compliance_r", title: "Static Compliance (ml / cm³) – Right" },
                { name: "static_compliance_l", title: "Static Compliance (ml / cm³) – Left" }
              ]
            },
            {
              type: "paired-text",
              name: "ecv",
              pairs: [
                { name: "ecv_r", title: "Ear Canal Volume (ml / cm³) – Right" },
                { name: "ecv_l", title: "Ear Canal Volume (ml / cm³) – Left" }
              ]
            }
          ]
        },
        
        /* ===================== OAE ===================== */
          {
              type: "accordion",
              name: "oae_section",
              label: "OAE Screening",
              defaultOpen: false,
              children: [

            /* ----------- OAE ----------- */
            {
              type: "row",
              columns: 2,
              fields: [
                {
                  type: "attach-file",
                  name: "oae_right_upload",
                  accept: "application/pdf,image/*",
                  title: "OAE – Right Ear",
                  multiple: false
                },
                {
                  type: "attach-file",
                  name: "oae_left_upload",
                  accept: "application/pdf,image/*",
                  title: "OAE – Left Ear",
                  multiple: false
                }
              ]
            },
            {
              type: "row",
              fields: [
                {
                  name: "oae_right",
                  type: "radio",
                  options: [
                    { label: "Pass", value: "pass" },
                    { label: "Refer", value: "refer" },
                    { label: "Could Not Test", value: "could_not_test" }
                  ]
                },
                {
                  name: "oae_left",
                  type: "radio",
                  options: [
                    { label: "Pass", value: "pass" },
                    { label: "Refer", value: "refer" },
                    { label: "Could Not Test", value: "could_not_test" }
                  ]
                }
              ]
            },

            /* ----------- DPOAE ----------- */
            {
              type: "row",
              columns: 2,
              fields: [
                {
                  type: "attach-file",
                  name: "dpoae_right_upload",
                  accept: "application/pdf,image/*",
                  title: "DPOAE – Right Ear",
                  multiple: false
                },
                {
                  type: "attach-file",
                  name: "dpoae_left_upload",
                  accept: "application/pdf,image/*",
                  title: "DPOAE – Left Ear",
                  multiple: false
                }
              ]
            },
            {
              type: "row",
              fields: [
                {
                  name: "dpoae_right",
                  type: "radio",
                  options: [
                    { label: "Pass", value: "pass" },
                    { label: "Refer", value: "refer" },
                    { label: "Could Not Test", value: "could_not_test" }
                  ]
                },
                {
                  name: "dpoae_left",
                  type: "radio",
                  options: [
                    { label: "Pass", value: "pass" },
                    { label: "Refer", value: "refer" },
                    { label: "Could Not Test", value: "could_not_test" }
                  ]
                }
              ]
            },

            /* ----------- TEOAE ----------- */
            {
              type: "row",
              columns: 2,
              fields: [
                {
                  type: "attach-file",
                  name: "teoae_right_upload",
                  accept: "application/pdf,image/*",
                  title: "TEOAE – Right Ear",
                  multiple: false
                },
                {
                  type: "attach-file",
                  name: "teoae_left_upload",
                  accept: "application/pdf,image/*",
                  title: "TEOAE – Left Ear",
                  multiple: false
                }
              ]
            },
            {
              type: "row",
              fields: [
                {
                  name: "teoae_right",
                  type: "radio",
                  options: [
                    { label: "Pass", value: "pass" },
                    { label: "Refer", value: "refer" },
                    { label: "Could Not Test", value: "could_not_test" }
                  ]
                },
                {
                  name: "teoae_left",
                  type: "radio",
                  options: [
                    { label: "Pass", value: "pass" },
                    { label: "Refer", value: "refer" },
                    { label: "Could Not Test", value: "could_not_test" }
                  ]
                }
              ]
            }
          ]
        },
          {
            type: "accordion",
            name: "acoustic_reflex_section",
            label: "Acoustic Reflex - Frequency (Hz)",
            defaultOpen: false,
            children: [
              {
                type: "refraction-12col",
                name: "acoustic_reflex_matrix",
                cornerLabel: "Frequency",
                cornerLikeGroupHeader: true,
                showColumnHeaders: true,
                groups: [{
                  label: "",
                  columns: [
                    { key: "Ipsilateral (Right Ear, dB HL)" },
                    { key: "Ipsilateral (Left Ear, dB HL)" },
                    { key: "Contralateral (Right Ear Stim)" },
                    { key: "Contralateral (Left Ear Stim)" }
                  ]
                }],
                rows: FREQUENCIES.map(freq => ({
                  value: freq,
                  label: `${freq} Hz`,
                  columns: [
                    { type: "select", options: reflexOptions },
                    { type: "select", options: reflexOptions },
                    { type: "select", options: reflexOptions },
                    { type: "select", options: reflexOptions }
                  ]
                }))
              }
            ]
          }
        ]
      },

      // =========================
      // ETF
      // =========================
      {
        title: null,
        fields: [
          {
            type: "accordion",
            name: "etf_section",
            label: "Eustachian Tube Function",
            defaultOpen: false,
            children: [
              {
                name: "etf_right",
                label: "Right Ear Peak Pressure (daPa)",
                type: "radio-matrix",
                options: [
                  { label: "Normal", value: 0 },
                  { label: "Reduced", value: 1 },
                  { label: "Absent", value: 2 },
                  { label: "Could Not Test", value: "could_not" }
                ]
              },
              {
                name: "etf_left",
                label: "Left Ear Peak Pressure (daPa)",
                type: "radio-matrix",
                options: [
                  { label: "Normal", value: 0 },
                  { label: "Reduced", value: 1 },
                  { label: "Absent", value: 2 },
                  { label: "Could Not Test", value: "could_not" }
                ]
              }
            ]
          }
        ]
      },

      // =========================
      // SPEECH
      // =========================
      {
        title: null,
        fields: [
          {
            type: "accordion",
            name: "type_of_test_section",
            label: "Speech Test",
            defaultOpen: false,
            children: [
              {
                type: "checkbox-group",
                name: "type_of_tests",
                label: "",
                options: [
                  { label: "Speech Reception Threshold (SRT)", value: "srt" },
                  { label: "Word Recognition Score (WRS)", value: "wrs" },
                  { label: "Listening Comprehension Task (LCT)", value: "lct" },
                  { label: "Auditory Processing Task (APT)", value: "apt" }
                ]
              },
              { type: "row", cols: 2, showIf: { field: "type_of_tests", includes: "srt" }, fields: [{ name: "srt_r", label: "SRT Right Ear", type: "input" }, { name: "srt_l", label: "SRT Left Ear", type: "input" }] },
              { type: "row", cols: 2, showIf: { field: "type_of_tests", includes: "wrs" }, fields: [{ name: "wrs_r", label: "WRS Right Ear", type: "input" }, { name: "wrs_l", label: "WRS Left Ear", type: "input" }] },
              { type: "row", cols: 2, showIf: { field: "type_of_tests", includes: "lct" }, fields: [{ name: "lct_r", label: "LCT Right Ear", type: "input" }, { name: "lct_l", label: "LCT Left Ear", type: "input" }] },
              { type: "row", cols: 2, showIf: { field: "type_of_tests", includes: "apt" }, fields: [{ name: "apt_r", label: "APT Right Ear", type: "input" }, { name: "apt_l", label: "APT Left Ear", type: "input" }] },
              { name: "remarks", label: "Remarks", type: "input", showIf: { field: "type_of_tests", notEmpty: true } }
            ]
          }
        ]
      },

      // =========================
      // ASSR
      // =========================
      {
        title: null,
        fields: [
          {
            type: "accordion",
            name: "assr_section",
            label: "Auditory Steady-State Response",
            defaultOpen: false,
            children: [
              {
                name: "assr_upload",
                type: "attach-file",
                accept: "application/pdf,image/*",
                title: "Upload ASSR File",
                multiple: false,
                previewSize: { width: 400, height: 400 },
                hideInputAfterSelect: true
              },
              {
                type: "refraction-12col",
                name: "assr_matrix",
                cornerLabel: "Frequency",
                cornerLikeGroupHeader: true,
                showColumnHeaders: true,
                groups: [{ label: "", columns: [{ key: "Right Ear Threshold (dB nHL)" }, { key: "Left Ear Threshold (dB nHL)" }] }],
                rows: FREQUENCIES.map(freq => ({
                  value: freq,
                  label: `${freq} Hz`,
                  columns: [{ type: "select", options: thresholdOptions }, { type: "select", options: thresholdOptions }]
                }))
              },
              { name: "assr_imp", label: "Impression", type: "input" }
            ]
          }
        ]
      },

      // =========================
      // ABR
      // =========================
      {
        title: null,
        fields: [
          {
            type: "accordion",
            name: "abr_section",
            label: "Auditory Brainstem Response",
            defaultOpen: false,
            children: [
              {
                name: "abr_upload",
                type: "attach-file",
                accept: "application/pdf,image/*",
                title: "Upload ABR File",
                multiple: false,
                previewSize: { width: 400, height: 400 },
                hideInputAfterSelect: true
              },
              {
                type: "refraction-12col",
                name: "abr_matrix",
                cornerLabel: "",
                cornerLikeGroupHeader: false,
                showColumnHeaders: true,
                groups: [{ label: "", columns: [{ key: "Right Ear" }, { key: "Left Ear" }] }],
                rows: [
                  { value: "abr",        label: "Auditory Brainstem Response", columns: [{ type: "input" }, { type: "input" }] },
                  { value: "impression", label: "Impression",                  columns: [{ type: "input" }, { type: "input" }] }
                ]
              }
            ]
          }
        ]
      },

      // =========================
      // ELECTROPHYSIOLOGY
      // =========================
      {
        title: null,
        fields: [
          {
            type: "accordion",
            name: "ep_section",
            label: "Electrophysiology For Hearing",
            defaultOpen: false,
            children: [
              {
                name: "ep_upload",
                type: "attach-file",
                accept: "application/pdf,image/*",
                title: "Upload Electrophysiology File",
                multiple: false,
                previewSize: { width: 400, height: 400 },
                hideInputAfterSelect: true
              },
              {
                type: "refraction-12col",
                name: "ep_matrix",
                cornerLabel: "",
                cornerLikeGroupHeader: false,
                showColumnHeaders: true,
                groups: [{ label: "", columns: [{ key: "Right Ear" }, { key: "Left Ear" }] }],
                rows: [
                  { value: "ep",         label: "Electrophysiology For Hearing", columns: [{ type: "input" }, { type: "input" }] },
                  { value: "impression", label: "Impression",                    columns: [{ type: "input" }, { type: "input" }] }
                ]
              }
            ]
          }
        ]
      },

      // =========================
      // INTERVENTION
      // =========================
      {
        title: null,
        fields: [
          {
            type: "accordion",
            name: "intervention_section",
            label: "Intervention",
            defaultOpen: false,
            children: [
              {
                name: "auditory_interventions",
               
                type: "checkbox-group",
                options: [
                  { label: "Hearing aids/assistive devices", value: "hearing_aids" },
                  { label: "Ear monitoring", value: "ear_monitoring" },
                  { label: "Fine tuning of hearing device", value: "fine_tuning" },
                  { label: "Auditory training", value: "auditory_training" },
                  { label: "Hearing device counselling", value: "hearing_device_counselling" },
                  { label: "Communication strategies Counselling", value: "communication_strategies" }
                ]
              },
              {
                type: "refraction-12col",
                name: "auditory_intervention_matrix",
                cornerLabel: "Intervention",
                cornerLikeGroupHeader: false,
                showColumnHeaders: true,
                groups: [{
                  label: "",
                  columns: [
                    { key: "Yes / No" },
                    { key: "Remarks" }
                  ]
                }],
                rows: (formValues) => {
                  const selected = formValues.auditory_interventions || [];
                  const allRows = [
                    {
                      value: "hearing_aids",
                      label: "Hearing aids/assistive devices",
                      columns: [
                        {
                          type: "select",
                          options: [
                            { label: "No", value: 0 },
                            { label: "Yes", value: 1 }
                          ]
                        },
                        { type: "input" }
                      ]
                    },
                    {
                      value: "ear_monitoring",
                      label: "Ear monitoring",
                      columns: [
                        {
                          type: "select",
                          options: [
                            { label: "No", value: 0 },
                            { label: "Yes", value: 1 }
                          ]
                        },
                        { type: "input" }
                      ]
                    },
                    {
                      value: "fine_tuning",
                      label: "Fine tuning of hearing device",
                      columns: [
                        {
                          type: "select",
                          options: [
                            { label: "No", value: 0 },
                            { label: "Yes", value: 1 }
                          ]
                        },
                        { type: "input" }
                      ]
                    },
                    {
                      value: "auditory_training",
                      label: "Auditory training",
                      columns: [
                        {
                          type: "select",
                          options: [
                            { label: "No", value: 0 },
                            { label: "Yes", value: 1 }
                          ]
                        },
                        { type: "input" }
                      ]
                    },
                    {
                      value: "hearing_device_counselling",
                      label: "Hearing device counselling",
                      columns: [
                        {
                          type: "select",
                          options: [
                            { label: "No", value: 0 },
                            { label: "Yes", value: 1 }
                          ]
                        },
                        { type: "input" }
                      ]
                    },
                    {
                      value: "communication_strategies",
                      label: "Communication strategies Counselling",
                      columns: [
                        {
                          type: "select",
                          options: [
                            { label: "No", value: 0 },
                            { label: "Yes", value: 1 }
                          ]
                        },
                        { type: "input" }
                      ]
                    }
                  ];

                  return allRows.filter((row) => selected.includes(row.value));
                }
              }
            ]
          }
        ]
      },

      // =========================
      // SPECIAL TEST
      // =========================
      {
        title: null,
        fields: [
          {
            type: "accordion",
            name: "special_test_section",
            label: "Special Test",
            defaultOpen: false,
            children: [
              {
                name: "special_upload",
                type: "attach-file",
                accept: "application/pdf,image/*",
                title: "Upload Special Test File",
                multiple: false,
                previewSize: { width: 400, height: 400 },
                hideInputAfterSelect: true
              },
              { name: "special_test", label: "Details", type: "input" }
            ]
          }
        ]
      }
    ]
  };

  return (
    <CommonFormBuilder
      schema={schema}
      values={{ ...values, mode }}
      layout="nested"
      onChange={handleChange}
      onAction={(type) => type === "back" && onBack?.()}
    />
  );
}
